import { Component, inject, Inject, OnInit } from '@angular/core';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Paciente } from '../pacientes';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatStepperModule } from '@angular/material/stepper';
import Swal from 'sweetalert2';
import { GraficasSignosVitales } from './graficas-signos-vitales/graficas-signos-vitales';
import { EstadoAtencionService } from '../../../core/services/estado-anterior.service';
import {
  CrearAtencionRequest,
  CrearSoloSignosRequest,
  AtencionService,
} from '../../../core/services/atencion.service';

@Component({
  selector: 'app-registro-signos-vitales',
  standalone: true,
  imports: [
    MatStepperModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatOption,
    MatDatepickerModule,
    DatePipe,
  ],
  templateUrl: './registro-signos-vitales.html',
  styleUrl: './registro-signos-vitales.scss',
})
export class RegistroSignosVitales implements OnInit {
  constructor(
    private dialogRef: MatDialogRef<RegistroSignosVitales>,
    @Inject(MAT_DIALOG_DATA) public paciente: Paciente,
    private dialog: MatDialog,
    private atencionService: AtencionService,
    private estadoAtencionService: EstadoAtencionService,
  ) {}

  esMenorDe16 = false;

  ngOnInit(): void {
    this.esMenorDe16 = this.calcularAniosCumplidos() < 16;

    if (this.esMenorDe16) {
      ['presionSistolica', 'presionDiastolica'].forEach((nombre) => {
        const control = this.firstFormGroup.get(nombre);
        control?.removeValidators(Validators.required);
        control?.updateValueAndValidity();
      });
    }
  }

  private calcularAniosCumplidos(): number {
    const fecha = this.paciente?.fechaNacimiento;
    if (!fecha) return 999;

    // Se toma solo la parte de la fecha para evitar desfases por zona horaria
    const [anio, mes, dia] = String(fecha).split('T')[0].split('-').map(Number);
    if (!anio || !mes || !dia) return 999;

    const hoy = new Date();
    let anios = hoy.getFullYear() - anio;

    if (hoy.getMonth() + 1 < mes || (hoy.getMonth() + 1 === mes && hoy.getDate() < dia)) {
      anios--;
    }

    return anios;
  }

  cerrar() {
    this.dialogRef.close();
  }

  readonly frecuenciaRespiratoriaDisponible = true;

  fechaActual: Date = new Date();
  // CALCULAR EDAD
  obtenerEdad(): string {
    const fechaNacimiento = this.paciente.fechaNacimiento;

    if (!fechaNacimiento) {
      return '';
    }

    const nacimiento = new Date(fechaNacimiento);
    const hoy = new Date();

    let anios = hoy.getFullYear() - nacimiento.getFullYear();
    let meses = hoy.getMonth() - nacimiento.getMonth();
    let dias = hoy.getDate() - nacimiento.getDate();

    if (dias < 0) {
      meses--;
      const mesAnterior = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
      dias += mesAnterior.getDate();
    }

    if (meses < 0) {
      anios--;
      meses += 12;
    }

    if (anios < 0) {
      return '';
    }

    if (anios === 0 && meses === 0) {
      return dias === 1 ? '1 día' : `${dias} días`;
    }

    if (anios === 0) {
      return meses === 1 ? '1 mes' : `${meses} meses`;
    }

    if (anios <= 10) {
      const textoAnios = anios === 1 ? '1 año' : `${anios} años`;
      if (meses === 0) return textoAnios;
      const textoMeses = meses === 1 ? '1 mes' : `${meses} meses`;
      return `${textoAnios} y ${textoMeses}`;
    }

    return anios === 1 ? '1 año' : `${anios} años`;
  }

  private fb = inject(FormBuilder);

  // SIGNOS VITALES
  firstFormGroup = this.fb.nonNullable.group({
    peso: [null, [Validators.required, Validators.min(0.5), Validators.max(300)]],

    frecuenciaCardiaca: [null, [Validators.required, Validators.min(40), Validators.max(300)]],

    presionSistolica: [null, [Validators.required, Validators.min(50), Validators.max(250)]],

    presionDiastolica: [null, [Validators.required, Validators.min(20), Validators.max(150)]],

    estatura: [null, [Validators.min(40), Validators.max(250)]],

    saturacionOxigeno: [null, [Validators.min(50), Validators.max(100)]],

    frecuenciaRespiratoria: [null, [Validators.min(10), Validators.max(100)]],

    glucosa: [null, [Validators.min(20), Validators.max(600)]],

    temperatura: [null, [Validators.min(25), Validators.max(45)]],

    observaciones: [''],
    prioridad: [''],
  });

  /* =======================================================
     GUARDAR SIGNOS VITALES
     ======================================================= */

  guardarSignosVitales(enviarSalaEspera: boolean): void {
    const sistolica = this.firstFormGroup.get('presionSistolica')?.value;
    const diastolica = this.firstFormGroup.get('presionDiastolica')?.value;

    if (this.esMenorDe16 && (sistolica === null) !== (diastolica === null)) {
      Swal.fire({
        icon: 'warning',
        title: 'Presión arterial incompleta',
        text: 'Captura la sistólica y la diastólica, o deja ambas vacías.',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    const rangosInvalidos = [
      {
        control: 'peso',
        nombre: 'Peso',
        unidad: 'kg',
      },
      {
        control: 'estatura',
        nombre: 'Talla',
        unidad: 'cm',
      },
      {
        control: 'saturacionOxigeno',
        nombre: 'Saturación de oxígeno',
        unidad: '%',
      },
      {
        control: ' frecuenciaRespiratoria',
        nombre: 'Frecuencia respiratoria',
        unidad: 'RPM',
      },
      {
        control: 'frecuenciaCardiaca',
        nombre: 'Frecuencia cardíaca',
        unidad: 'lpm',
      },
      {
        control: 'glucosa',
        nombre: 'Glucosa',
        unidad: 'mg/dL',
      },
      {
        control: 'temperatura',
        nombre: 'Temperatura',
        unidad: '°C',
      },
      {
        control: 'presionSistolica',
        nombre: 'Presión sistólica',
        unidad: 'mmHg',
      },
      {
        control: 'presionDiastolica',
        nombre: 'Presión diastólica',
        unidad: 'mmHg',
      },
    ];

    const campoFueraDeRango = rangosInvalidos.find(
      (item) =>
        this.firstFormGroup.get(item.control)?.hasError('min') ||
        this.firstFormGroup.get(item.control)?.hasError('max'),
    );

    if (campoFueraDeRango) {
      const control = this.firstFormGroup.get(campoFueraDeRango.control);

      control?.markAsTouched();

      Swal.fire({
        icon: 'warning',
        title: 'Valor fuera de rango',
        text: `El valor ingresado en ${campoFueraDeRango.nombre} está fuera del rango permitido.`,
        showConfirmButton: false,
        timer: 2500,
        customClass: {
          title: 'mi-swal-title',
          htmlContainer: 'mi-swal-text',
        },
      }).then(() => {
        this.enfocarCampo(campoFueraDeRango.control);
      });

      return;
    }

    const primerCampoInvalido = Object.entries(this.firstFormGroup.controls).find(
      ([_, control]) => control.invalid,
    );

    if (primerCampoInvalido) {
      const [nombreControl, control] = primerCampoInvalido;

      control.markAsTouched();

      Swal.fire({
        icon: 'error',
        title: 'Información incompleta',
        text: 'Completa los campos obligatorios.',
        showConfirmButton: false,
        timer: 2500,
        customClass: {
          title: 'mi-swal-title',
          htmlContainer: 'mi-swal-text',
        },
      }).then(() => {
        this.enfocarCampo(nombreControl);
      });

      return;
    }

    // =====================================================
    // VALIDAR PRIORIDAD
    // SOLAMENTE PARA SALA DE ESPERA
    // =====================================================

    if (enviarSalaEspera && !this.firstFormGroup.get('prioridad')?.value) {
      const controlPrioridad = this.firstFormGroup.get('prioridad');

      controlPrioridad?.markAsTouched();

      Swal.fire({
        icon: 'warning',
        title: 'Selecciona una prioridad',
        text: 'Para enviar al paciente a sala de espera debes seleccionar una prioridad.',
        showConfirmButton: false,
        timer: 2500,
        customClass: {
          title: 'mi-swal-title',
          htmlContainer: 'mi-swal-text',
        },
      }).then(() => {
        this.enfocarCampo('prioridad');
      });

      return;
    }

    // =====================================================
    // VALIDAR ID DEL PACIENTE
    // =====================================================

    if (!this.paciente.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontró el ID del paciente.',
        showConfirmButton: false,
        timer: 2500,
        customClass: {
          title: 'mi-swal-title',
          htmlContainer: 'mi-swal-text',
        },
      });

      return;
    }

    // =====================================================
    // OBTENER DATOS
    // =====================================================

    const datos = this.firstFormGroup.getRawValue();

    // =====================================================
    // CONVERTIR ESTATURA
    // =====================================================

    const estaturaMetros = datos.estatura !== null ? Number(datos.estatura) / 100 : null;

    const frecuenciaRespiratoria =
      datos.frecuenciaRespiratoria !== null ? Number(datos.frecuenciaRespiratoria) : null;
    // =====================================================
    // SIGNOS VITALES
    // =====================================================

    const signosVitales = {
      peso: datos.peso !== null ? Number(datos.peso) : null,

      estatura: estaturaMetros,

      saturacionOxigeno: datos.saturacionOxigeno !== null ? Number(datos.saturacionOxigeno) : null,

      ...(this.frecuenciaRespiratoriaDisponible ? { frecuenciaRespiratoria } : {}),

      frecuenciaCardiaca:
        datos.frecuenciaCardiaca !== null ? Number(datos.frecuenciaCardiaca) : null,

      glucosa: datos.glucosa !== null ? Number(datos.glucosa) : null,

      temperatura: datos.temperatura !== null ? Number(datos.temperatura) : null,

      presionSistolica: datos.presionSistolica !== null ? Number(datos.presionSistolica) : null,

      presionDiastolica: datos.presionDiastolica !== null ? Number(datos.presionDiastolica) : null,

      observaciones: datos.observaciones ?? '',
    };

    // =====================================================
    // ENVIAR A SALA DE ESPERA
    // POST /atencion
    // =====================================================

    if (enviarSalaEspera) {
      const datosAtencion: CrearAtencionRequest = {
        pacienteId: this.paciente.id,

        prioridad: this.convertirPrioridad(datos.prioridad),

        signosVitales,
      };

      // console.log('========== POST /ATENCION ==========');

      // console.log(datosAtencion);

      this.atencionService.crearAtencion(datosAtencion).subscribe({
        next: (respuesta) => {
          // console.log('========== ATENCIÓN CREADA ==========');

          // console.log(respuesta);

          const atencionCreada = respuesta?.data;

          // ==========================================
          // VALIDAR RESPUESTA DEL BACKEND
          // ==========================================

          if (!atencionCreada?.id) {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'El servidor no devolvió la atención creada.',
              showConfirmButton: false,
              timer: 2500,
            });

            return;
          }

          // ==========================================
          // GUARDAR ESTADO LOCAL
          // ==========================================

          this.estadoAtencionService.setEstado(atencionCreada.id, 'EN_ESPERA');

          // ==========================================
          // MENSAJE
          // ==========================================

          Swal.fire({
            icon: 'success',

            title: 'Paciente enviado a sala de espera',

            text: 'El paciente se agregó correctamente a la sala de espera.',

            showConfirmButton: false,
            timer: 2500,

            customClass: {
              title: 'mi-swal-title',
              htmlContainer: 'mi-swal-text',
            },
          }).then(() => {
            this.dialogRef.close(atencionCreada);
          });
        },

        error: (error) => {
          // console.error('========== ERROR POST /ATENCION ==========');

          // console.error(error);

          Swal.fire({
            icon: 'error',

            title: 'No fue posible enviar al paciente',

            text: error?.error?.message ?? 'No fue posible enviar al paciente a la sala de espera.',

            showConfirmButton: false,
            timer: 2500,

            customClass: {
              title: 'mi-swal-title',
              htmlContainer: 'mi-swal-text',
            },
          });
        },
      });

      return;
    }

    // =====================================================
    // SOLAMENTE GUARDAR SIGNOS VITALES
    // POST /atencion/solo-signos
    // =====================================================

    const datosSoloSignos: CrearSoloSignosRequest = {
      pacienteId: this.paciente.id,

      signosVitales,
    };

    // console.log('========== POST /ATENCION/SOLO-SIGNOS ==========');

    // console.log(datosSoloSignos);

    this.atencionService.guardarSoloSignos(datosSoloSignos).subscribe({
      next: (respuesta) => {
        // console.log('========== SIGNOS VITALES GUARDADOS ==========');

        // console.log(respuesta);

        const atencionCreada = respuesta?.data;

        // ==========================================
        // MENSAJE
        // ==========================================

        Swal.fire({
          icon: 'success',

          title: 'Información guardada',

          text: 'Los signos vitales se guardaron correctamente.',

          showConfirmButton: false,
          timer: 2500,

          customClass: {
            title: 'mi-swal-title',
            htmlContainer: 'mi-swal-text',
          },
        }).then(() => {
          this.dialogRef.close(atencionCreada);
        });
      },

      error: (error) => {
        // console.error('========== ERROR POST /ATENCION/SOLO-SIGNOS ==========');

        // console.error(error);

        Swal.fire({
          icon: 'error',

          title: 'No fue posible guardar la información',

          text: error?.error?.message ?? 'No fue posible guardar los signos vitales.',

          showConfirmButton: false,
          timer: 2500,

          customClass: {
            title: 'mi-swal-title',
            htmlContainer: 'mi-swal-text',
          },
        });
      },
    });
  }

  // CONVERTIR PRIORIDAD
  // ========================================

  private convertirPrioridad(prioridad: string): string {
    switch (prioridad) {
      case 'Emergencia':
        return 'rojo';

      case 'UrgenteMayor':
        return 'naranja';

      case 'Urgente':
        return 'amarillo';

      case 'UrgenciaMenor':
        return 'verde';

      case 'SinUrgencia':
        return 'azul';

      default:
        return 'azul';
    }
  }

  private enfocarCampo(controlName: string): void {
    setTimeout(() => {
      const elemento = document.querySelector(
        `[formControlName="${controlName}"]`,
      ) as HTMLElement | null;

      if (elemento) {
        elemento.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        elemento.focus();
      }
    }, 1000);
  }

  abrirGragicaSignosVitales() {
    this.dialogRef.close();

    this.dialog.open(GraficasSignosVitales, {
      width: '95vw',
      height: '90vh',
      maxWidth: '95vw',
      data: this.paciente,
    });
  }
}
